import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
  Collapse
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // stato per "Password dimenticata?"
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password);
      if (response.two_fa_required) {
        navigate('/verify-2fa');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError('Credenziali non valide o errore di rete.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    setForgotLoading(true);
    try {
      const res = await authService.forgotPassword(forgotEmail);
      setForgotSuccess(res.message || 'Se l’email è registrata, riceverai un link per reimpostare la password.');
    } catch (err: any) {
      setForgotError(err?.response?.data?.error || 'Si è verificato un errore. Riprova più tardi.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <Paper elevation={3} sx={{ padding: 4, width: 400, maxWidth: '90%' }}>
        <Typography variant="h5" gutterBottom align="center">Accesso</Typography>

        {/* --- Form login --- */}
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Accedi'}
          </Button>
        </form>

        {/* --- Link che apre la schedina --- */}
        <Box mt={2} textAlign="center">
          <Link component="button" type="button" underline="hover" onClick={() => setShowForgot((s) => !s)}>
            Password dimenticata?
          </Link>
        </Box>

        {/* --- Schedina inline per inserire l'email e inviare il reset --- */}
        <Collapse in={showForgot} unmountOnExit>
          <Box
            component="form"
            onSubmit={handleForgotSubmit}
            mt={2}
            sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Recupero password
            </Typography>

            <TextField
              label="Email account"
              type="email"
              fullWidth
              margin="dense"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              required
            />

            {forgotError && <Alert severity="error" sx={{ mt: 1 }}>{forgotError}</Alert>}
            {forgotSuccess && <Alert severity="success" sx={{ mt: 1 }}>{forgotSuccess}</Alert>}

            <Button
              type="submit"
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              disabled={forgotLoading || !forgotEmail}
            >
              {forgotLoading ? 'Invio…' : 'Invia link di recupero'}
            </Button>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default LoginPage;
